/**
 * Writing Assistant component for LLM-powered text enhancement.
 *
 * Features:
 * - Side-by-side comparison of original and enhanced text
 * - Streaming enhancement with real-time updates
 * - Loading indicator during enhancement
 * - Error handling and display
 * - Empty text validation
 * - Responsive layout (stacked on mobile, side-by-side on desktop)
 */
import { useRef, useEffect, useState, useMemo } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { marked } from "marked";
import * as Diff from "diff";
import { Loader2, Sparkles, AlertCircle, Check, X, Maximize2, Minimize2, ChevronDown, ChevronRight, GitCompare, FileText, Edit3, Save } from "lucide-react";
import { Button } from "../ui/Button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/Select";
import { useWritingAssistant, LLMModelChoice } from "../../hooks/useWritingAssistant";
import { listPrompts, createPrompt, updatePrompt, SystemPrompt, DEFAULT_ENHANCEMENT_PROMPT, PROMPT_PLACEHOLDERS } from "../../lib/api/writingAssistant";
import { useIsAdmin } from "../../hooks/useAuthorization";
import type { DeltaStatic, Sources } from "quill";

// Configure marked for safe HTML output
marked.setOptions({
  breaks: true, // Convert \n to <br>
  gfm: true,    // GitHub Flavored Markdown
});

// Custom styles for scrollable Quill editors
const quillScrollStyles = `
  .writing-assistant-editor {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
  .writing-assistant-editor .ql-toolbar {
    flex-shrink: 0;
    border-left: 0;
    border-right: 0;
    border-top: 0;
  }
  .writing-assistant-editor .ql-container {
    flex: 1;
    overflow: hidden;
    border: 0;
  }
  .writing-assistant-editor .ql-editor {
    height: 100%;
    overflow-y: auto;
  }
`;

/**
 * Props for the WritingAssistant component.
 */
export interface WritingAssistantProps {
  /** The question/query that the document should answer */
  question: string;
  /** Initial text to populate the original text area */
  initialText?: string;
  /** Initial formatted content (Quill Delta) */
  initialFormattedContent?: DeltaStatic;
  /** Callback when user accepts the enhanced text */
  onAcceptEnhanced?: (text: string, formattedContent?: DeltaStatic) => void;
  /** Callback when user chooses to use original text */
  onUseOriginal?: (text: string, formattedContent?: DeltaStatic) => void;
  /** Maximum character limit */
  maxCharacters?: number;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Custom prompt ID to use for enhancement */
  promptId?: string;
  /** Label for the original text area */
  originalLabel?: string;
  /** Label for the enhanced text area */
  enhancedLabel?: string;
}

/**
 * Writing Assistant component for enhancing text using LLM.
 *
 * @example
 * ```tsx
 * <WritingAssistant
 *   initialText={record.content}
 *   onAcceptEnhanced={(text) => setContent(text)}
 *   maxCharacters={10000}
 * />
 * ```
 */
export function WritingAssistant({
  question,
  initialText = "",
  initialFormattedContent,
  onAcceptEnhanced,
  onUseOriginal,
  maxCharacters = 10000,
  disabled = false,
  promptId,
  originalLabel = "Original Text",
  enhancedLabel = "Enhanced Text",
}: WritingAssistantProps) {
  const originalQuillRef = useRef<ReactQuill>(null);
  const enhancedQuillRef = useRef<ReactQuill>(null);
  const customPromptRef = useRef<HTMLTextAreaElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [selectedModel, setSelectedModel] = useState<LLMModelChoice>("llama");
  const [prompts, setPrompts] = useState<SystemPrompt[]>([]);
  const [selectedPromptId, setSelectedPromptId] = useState<string | undefined>(promptId);
  const [loadingPrompts, setLoadingPrompts] = useState(true);

  // Custom prompt state for embedded prompt testing (FR-014)
  const [customPrompt, setCustomPrompt] = useState<string>("");
  const [showCustomPromptInput, setShowCustomPromptInput] = useState(false);
  const [showPromptPreview, setShowPromptPreview] = useState(false);

  // Save to Library state (FR-015, Admin Only)
  const isAdmin = useIsAdmin();
  const [showSavePromptModal, setShowSavePromptModal] = useState(false);
  const [savePromptName, setSavePromptName] = useState("");
  const [savePromptDescription, setSavePromptDescription] = useState("");
  const [isSavingPrompt, setIsSavingPrompt] = useState(false);
  const [savePromptError, setSavePromptError] = useState<string | null>(null);

  // Editing existing prompt state
  const [editingPromptId, setEditingPromptId] = useState<string | null>(null);
  const [editingPromptName, setEditingPromptName] = useState<string>("");

  const {
    originalText,
    setOriginalText,
    enhancedText,
    setEnhancedText,
    isEnhancing,
    error,
    processingTimeMs,
    enhanceStreaming,
    canEnhance,
    abort,
    setQuestion,
  } = useWritingAssistant({
    initialQuestion: question,
    initialText,
    onEnhanceComplete: (response) => {
      console.log(`Enhancement completed in ${response.processing_time_ms}ms`);
    },
    onEnhanceError: (err) => {
      console.error("Enhancement error:", err);
    },
  });

  // Keep the hook's question in sync with the prop
  useEffect(() => {
    setQuestion(question);
  }, [question, setQuestion]);

  // Load prompts on mount
  useEffect(() => {
    const fetchPrompts = async () => {
      try {
        setLoadingPrompts(true);
        const fetchedPrompts = await listPrompts(false);
        setPrompts(fetchedPrompts);
        // If no prompt selected and there's a default, select it
        if (!selectedPromptId) {
          const defaultPrompt = fetchedPrompts.find(p => p.is_default);
          if (defaultPrompt) {
            setSelectedPromptId(defaultPrompt.id);
          }
        }
      } catch (err) {
        console.error("Failed to load prompts:", err);
      } finally {
        setLoadingPrompts(false);
      }
    };
    fetchPrompts();
  }, []);

  // Convert markdown to HTML for display in Quill editor
  const enhancedHtml = useMemo(() => {
    if (!enhancedText) return "";
    // marked.parse returns string | Promise<string>, but with sync options it's string
    return marked.parse(enhancedText) as string;
  }, [enhancedText]);

  // Compute word-level diff between original and enhanced text
  const diffResult = useMemo(() => {
    if (!originalText || !enhancedText) return null;
    // Strip HTML tags from enhanced text for comparison
    const enhancedPlainText = enhancedHtml.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    return Diff.diffWords(originalText, enhancedPlainText);
  }, [originalText, enhancedText, enhancedHtml]);

  // Get the current prompt text for preview (custom > selected > default)
  const currentPromptText = useMemo(() => {
    if (customPrompt.trim()) {
      return customPrompt;
    }
    if (selectedPromptId) {
      const selectedPrompt = prompts.find(p => p.id === selectedPromptId);
      return selectedPrompt?.prompt_text || DEFAULT_ENHANCEMENT_PROMPT;
    }
    return DEFAULT_ENHANCEMENT_PROMPT;
  }, [customPrompt, selectedPromptId, prompts]);

  // Get the current prompt name for display
  const currentPromptName = useMemo(() => {
    if (customPrompt.trim()) {
      return "Custom Prompt";
    }
    if (selectedPromptId) {
      const selectedPrompt = prompts.find(p => p.id === selectedPromptId);
      return selectedPrompt?.name || "Default Prompt";
    }
    return "Default Prompt";
  }, [customPrompt, selectedPromptId, prompts]);

  // Quill editor modules configuration
  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ],
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "link",
  ];

  // Sync initial formatted content with Quill
  useEffect(() => {
    if (initialFormattedContent && originalQuillRef.current) {
      const editor = originalQuillRef.current.getEditor();
      editor.setContents(initialFormattedContent);
    }
  }, [initialFormattedContent]);

  /**
   * Handle original text change from Quill editor.
   * We store plain text for the original since that's what gets sent to the API.
   */
  const handleOriginalChange = (
    _content: string,
    _delta: DeltaStatic,
    _source: Sources,
    editor: ReactQuill.UnprivilegedEditor
  ) => {
    const plainText = editor.getText().trim();
    setOriginalText(plainText);
  };

  /**
   * Handle enhanced text change from Quill editor (for manual edits).
   * We store HTML content to preserve formatting.
   */
  const handleEnhancedChange = (
    content: string,
    _delta: DeltaStatic,
    _source: Sources,
    _editor: ReactQuill.UnprivilegedEditor
  ) => {
    // content is the HTML string from ReactQuill
    // Only update if content is meaningful (not just empty tags)
    const strippedContent = content.replace(/<[^>]*>/g, '').trim();
    if (strippedContent || content === '') {
      setEnhancedText(content);
    }
  };

  /**
   * Start the enhancement process.
   * Passes custom prompt text if entered, otherwise uses selected prompt ID.
   */
  const handleEnhance = async () => {
    if (!canEnhance || disabled) return;

    // Validate character limit
    if (originalText.length > maxCharacters) {
      return;
    }

    // Pass custom prompt if entered, otherwise use selected prompt ID
    await enhanceStreaming(selectedPromptId, selectedModel, customPrompt.trim() || undefined);
  };

  /**
   * Accept the enhanced text.
   */
  const handleAcceptEnhanced = () => {
    if (!enhancedHtml) return;

    const formattedContent = enhancedQuillRef.current
      ?.getEditor()
      .getContents();
    // Pass the HTML content (converted from markdown) to preserve formatting
    onAcceptEnhanced?.(enhancedHtml, formattedContent);
  };

  /**
   * Use the original text.
   */
  const handleUseOriginal = () => {
    const formattedContent = originalQuillRef.current
      ?.getEditor()
      .getContents();
    onUseOriginal?.(originalText, formattedContent);
  };

  /**
   * Handle custom prompt change with mutual exclusion.
   * When user enters custom prompt, clear saved prompt selection.
   */
  const handleCustomPromptChange = (text: string) => {
    setCustomPrompt(text);
    if (text.trim()) {
      setSelectedPromptId(undefined); // Clear saved selection
    }
  };

  /**
   * Insert a placeholder at the current cursor position in the custom prompt textarea.
   */
  const insertPlaceholder = (placeholder: string) => {
    const textarea = customPromptRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = customPrompt;

    const newText = text.substring(0, start) + placeholder + text.substring(end);
    setCustomPrompt(newText);
    setSelectedPromptId(undefined); // Clear saved selection

    // Restore focus and set cursor position after the inserted placeholder
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = start + placeholder.length;
    }, 0);
  };

  /**
   * Load an existing prompt into the custom prompt editor for editing.
   */
  const loadPromptForEditing = (promptId: string) => {
    const prompt = prompts.find(p => p.id === promptId);
    if (!prompt) return;

    setCustomPrompt(prompt.prompt_text);
    setEditingPromptId(prompt.id);
    setEditingPromptName(prompt.name);
    setSelectedPromptId(undefined); // Clear selection since we're editing
    setShowCustomPromptInput(true);
  };

  /**
   * Clear the editing state and reset to fresh custom prompt mode.
   */
  const clearEditingState = () => {
    setEditingPromptId(null);
    setEditingPromptName("");
  };

  /**
   * Handle updating an existing prompt (creates a new version).
   */
  const handleUpdateExistingPrompt = async () => {
    if (!editingPromptId || !customPrompt.trim()) return;

    setIsSavingPrompt(true);
    setSavePromptError(null);

    try {
      await updatePrompt(editingPromptId, {
        prompt_text: customPrompt.trim(),
      });

      // Refresh prompts list
      const updatedPrompts = await listPrompts(false);
      setPrompts(updatedPrompts);

      // Select the updated prompt and clear custom prompt
      setSelectedPromptId(editingPromptId);
      setCustomPrompt("");
      setShowCustomPromptInput(false);
      clearEditingState();

      // Close modal if open
      setShowSavePromptModal(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to update prompt";
      setSavePromptError(errorMessage);
      console.error("Failed to update prompt:", err);
    } finally {
      setIsSavingPrompt(false);
    }
  };

  /**
   * Handle saving custom prompt to library (Admin only - FR-015).
   */
  const handleSavePromptToLibrary = async () => {
    if (!customPrompt.trim() || !savePromptName.trim()) return;

    setIsSavingPrompt(true);
    setSavePromptError(null);

    try {
      const newPrompt = await createPrompt({
        name: savePromptName.trim(),
        prompt_text: customPrompt.trim(),
        description: savePromptDescription.trim() || undefined,
      });

      // Refresh prompts list
      const updatedPrompts = await listPrompts(false);
      setPrompts(updatedPrompts);

      // Select the new prompt and clear custom prompt
      setSelectedPromptId(newPrompt.id);
      setCustomPrompt("");
      setShowCustomPromptInput(false);
      clearEditingState();

      // Reset modal state
      setShowSavePromptModal(false);
      setSavePromptName("");
      setSavePromptDescription("");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to save prompt";
      setSavePromptError(errorMessage);
      console.error("Failed to save prompt:", err);
    } finally {
      setIsSavingPrompt(false);
    }
  };

  /**
   * Handle saved prompt selection with mutual exclusion.
   * When user selects a saved prompt, clear custom prompt.
   * Special case: "new-custom" opens the custom prompt editor.
   */
  const handlePromptSelect = (id: string | undefined) => {
    if (id === "new-custom") {
      // Open custom prompt editor for a new prompt
      setSelectedPromptId(undefined);
      setCustomPrompt("");
      clearEditingState();
      setShowCustomPromptInput(true);
      return;
    }

    setSelectedPromptId(id);
    if (id) {
      setCustomPrompt(""); // Clear custom input
      setShowCustomPromptInput(false);
      clearEditingState(); // Clear editing state
    }
  };

  /**
   * Check if original text exceeds character limit.
   */
  const isOverLimit = originalText.length > maxCharacters;

  /**
   * Get character count display class based on limit.
   */
  const getCharCountClass = () => {
    const ratio = originalText.length / maxCharacters;
    if (ratio > 1) return "text-red-600";
    if (ratio > 0.9) return "text-yellow-600";
    return "text-gray-500";
  };

  // Dynamic height class based on expanded state
  const editorHeightClass = isExpanded
    ? "h-[600px]"
    : "h-[400px]";

  return (
    <div className="space-y-4 w-full max-w-none">
      {/* Inject custom styles for scrollable editors */}
      <style>{quillScrollStyles}</style>

      {/* Error Display */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 p-4 text-red-700">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Header with expand toggle */}
      <div className="flex items-center justify-end">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
        >
          {isExpanded ? (
            <>
              <Minimize2 className="h-3.5 w-3.5" />
              Collapse editors
            </>
          ) : (
            <>
              <Maximize2 className="h-3.5 w-3.5" />
              Expand editors
            </>
          )}
        </button>
      </div>

      {/* Side-by-side layout - wider container */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Original Text Panel */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">
              {originalLabel}
            </label>
            <span className={`text-xs ${getCharCountClass()}`}>
              {originalText.length.toLocaleString()} / {maxCharacters.toLocaleString()}
            </span>
          </div>
          <div className={`${editorHeightClass} rounded-lg border border-gray-300 bg-white overflow-hidden`}>
            <ReactQuill
              ref={originalQuillRef}
              defaultValue={initialFormattedContent || initialText}
              onChange={handleOriginalChange}
              modules={modules}
              formats={formats}
              theme="snow"
              readOnly={isEnhancing || disabled}
              placeholder="Enter your text here..."
              className="writing-assistant-editor [&_.ql-toolbar]:bg-gray-50 [&_.ql-container]:bg-white"
            />
          </div>
          {isOverLimit && (
            <p className="text-xs text-red-600">
              Text exceeds maximum length of {maxCharacters.toLocaleString()} characters
            </p>
          )}
        </div>

        {/* Enhanced Text Panel */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">
              {enhancedLabel}
            </label>
            {processingTimeMs && (
              <span className="text-xs text-gray-500">
                Generated in {(processingTimeMs / 1000).toFixed(1)}s
              </span>
            )}
          </div>
          <div className={`${editorHeightClass} rounded-lg border-2 border-indigo-200 bg-indigo-50/30 overflow-hidden`}>
            <ReactQuill
              ref={enhancedQuillRef}
              value={enhancedHtml}
              onChange={handleEnhancedChange}
              modules={modules}
              formats={formats}
              theme="snow"
              readOnly={isEnhancing || disabled}
              placeholder={
                isEnhancing
                  ? "Enhancing your text..."
                  : "Enhanced text will appear here..."
              }
              className="writing-assistant-editor [&_.ql-toolbar]:bg-indigo-50 [&_.ql-container]:bg-transparent"
            />
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        {/* Enhance Button, Model Selector, and Prompt Selector */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Prompt Selector */}
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-gray-500" />
            <Select
              value={showCustomPromptInput ? "new-custom" : (selectedPromptId || "default")}
              onValueChange={(value) => handlePromptSelect(value === "default" ? undefined : value)}
              disabled={isEnhancing || disabled || loadingPrompts}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder={loadingPrompts ? "Chargement..." : "Sélectionner un prompt"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Prompt par défaut</SelectItem>
                {prompts.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}{p.is_default ? " (défaut)" : ""}
                  </SelectItem>
                ))}
                {isAdmin && (
                  <SelectItem value="new-custom">+ Nouveau prompt</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Model Selector */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Model:</label>
            <Select
              value={selectedModel}
              onValueChange={(value) => setSelectedModel(value as LLMModelChoice)}
              disabled={isEnhancing || disabled}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="llama">Llama 3.1 70B</SelectItem>
                <SelectItem value="openai">GPT OSS 120B</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            type="button"
            onClick={handleEnhance}
            disabled={!canEnhance || isOverLimit || disabled}
            className="gap-2"
          >
            {isEnhancing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Enhancing...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                Enhance with AI
              </>
            )}
          </Button>

          {isEnhancing && (
            <Button type="button" variant="ghost" size="sm" onClick={abort}>
              Cancel
            </Button>
          )}
        </div>

        {/* Selection Buttons */}
        {enhancedText && !isEnhancing && (
          <div className="flex items-center gap-2">
            {onUseOriginal && (
              <Button
                type="button"
                variant="outline"
                onClick={handleUseOriginal}
                disabled={disabled}
                className="gap-2"
              >
                <X className="h-4 w-4" />
                Use Original
              </Button>
            )}
            {onAcceptEnhanced && (
              <Button
                type="button"
                variant="primary"
                onClick={handleAcceptEnhanced}
                disabled={disabled}
                className="gap-2"
              >
                <Check className="h-4 w-4" />
                Use Enhanced
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Prompt Preview - Always visible collapsible container */}
      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        <button
          type="button"
          onClick={() => setShowPromptPreview(!showPromptPreview)}
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
        >
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">
              {currentPromptName}
            </span>
            {editingPromptId && (
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                En modification
              </span>
            )}
          </div>
          {showPromptPreview ? (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-500" />
          )}
        </button>
        {showPromptPreview && (
          <>
            <div className="p-4 max-h-[300px] overflow-y-auto border-t border-gray-100">
              <pre className="text-sm text-gray-600 whitespace-pre-wrap font-mono bg-gray-50 rounded-md p-3 border border-gray-100">
                {currentPromptText}
              </pre>
            </div>
            {/* Edit button for saved prompts (Admin only) */}
            {isAdmin && selectedPromptId && !showCustomPromptInput && (
              <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    loadPromptForEditing(selectedPromptId);
                    setShowPromptPreview(false);
                  }}
                  disabled={isEnhancing || disabled}
                  className="gap-1.5"
                >
                  <Edit3 className="h-3.5 w-3.5" />
                  Modifier ce prompt
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Custom Prompt Input - Collapsible (FR-014) */}
      {showCustomPromptInput && (
        <div className={`rounded-lg border p-4 space-y-3 ${
          editingPromptId
            ? "border-amber-200 bg-amber-50/30"
            : "border-indigo-200 bg-indigo-50/30"
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">
                {editingPromptId ? "Modifier le prompt" : "Prompt personnalisé"}
              </label>
              {editingPromptId && (
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                  {editingPromptName}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {editingPromptId && (
                <button
                  type="button"
                  onClick={() => {
                    clearEditingState();
                    // Keep the custom prompt but mark as new
                  }}
                  className="text-xs text-amber-600 hover:text-amber-700"
                >
                  Créer nouveau
                </button>
              )}
              {customPrompt.trim() && (
                <button
                  type="button"
                  onClick={() => {
                    setCustomPrompt("");
                    clearEditingState();
                    // Restore default prompt selection if available
                    const defaultPrompt = prompts.find(p => p.is_default);
                    if (defaultPrompt) {
                      setSelectedPromptId(defaultPrompt.id);
                    }
                  }}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Effacer
                </button>
              )}
            </div>
          </div>

          {/* Placeholder Insert Buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-500">Insérer :</span>
            <button
              type="button"
              onClick={() => insertPlaceholder(PROMPT_PLACEHOLDERS.question)}
              disabled={isEnhancing || disabled}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-mono bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {PROMPT_PLACEHOLDERS.question}
              <span className="text-gray-400 font-sans">(titre)</span>
            </button>
            <button
              type="button"
              onClick={() => insertPlaceholder(PROMPT_PLACEHOLDERS.answer)}
              disabled={isEnhancing || disabled}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-mono bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {PROMPT_PLACEHOLDERS.answer}
              <span className="text-gray-400 font-sans">(contenu)</span>
            </button>
          </div>

          <textarea
            ref={customPromptRef}
            value={customPrompt}
            onChange={(e) => handleCustomPromptChange(e.target.value)}
            placeholder="Entrez votre prompt personnalisé ici. Utilisez {{question}} pour le titre et {{answer}} pour le contenu..."
            disabled={isEnhancing || disabled}
            rows={6}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-mono shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed resize-y min-h-[150px]"
          />
          {/* Error display for save/update operations */}
          {savePromptError && !showSavePromptModal && (
            <div className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-red-700 text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{savePromptError}</span>
              <button
                type="button"
                onClick={() => setSavePromptError(null)}
                className="ml-auto text-red-500 hover:text-red-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-gray-500 flex-1 min-w-[200px]">
              Les placeholders <code className="bg-gray-100 px-1 rounded">{"{{question}}"}</code> et <code className="bg-gray-100 px-1 rounded">{"{{answer}}"}</code> seront remplacés dynamiquement.
            </p>
            {/* Save buttons - Admin only */}
            {isAdmin && customPrompt.trim() && (
              <div className="flex items-center gap-2">
                {/* Update existing prompt button */}
                {editingPromptId && (
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={handleUpdateExistingPrompt}
                    disabled={isEnhancing || disabled || isSavingPrompt}
                    className="gap-1.5"
                  >
                    {isSavingPrompt ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Save className="h-3.5 w-3.5" />
                    )}
                    Mettre à jour
                  </Button>
                )}
                {/* Save as new prompt button */}
                <Button
                  type="button"
                  variant={editingPromptId ? "outline" : "primary"}
                  size="sm"
                  onClick={() => {
                    setSavePromptError(null);
                    setShowSavePromptModal(true);
                  }}
                  disabled={isEnhancing || disabled || isSavingPrompt}
                  className="gap-1.5"
                >
                  <Save className="h-3.5 w-3.5" />
                  {editingPromptId ? "Enregistrer comme nouveau" : "Enregistrer"}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Save Prompt Modal (FR-015) */}
      {showSavePromptModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Save Prompt to Library</h3>
              <button
                type="button"
                onClick={() => {
                  setShowSavePromptModal(false);
                  setSavePromptName("");
                  setSavePromptDescription("");
                  setSavePromptError(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {savePromptError && (
              <div className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-red-700 text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{savePromptError}</span>
              </div>
            )}

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prompt Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={savePromptName}
                  onChange={(e) => setSavePromptName(e.target.value)}
                  placeholder="e.g., Formal Business Writing"
                  disabled={isSavingPrompt}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={savePromptDescription}
                  onChange={(e) => setSavePromptDescription(e.target.value)}
                  placeholder="Brief description of what this prompt does..."
                  disabled={isSavingPrompt}
                  rows={2}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                />
              </div>
              <div className="bg-gray-50 rounded-md p-3">
                <p className="text-xs font-medium text-gray-500 mb-1">Prompt Preview</p>
                <p className="text-xs text-gray-600 line-clamp-3">{customPrompt}</p>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowSavePromptModal(false);
                  setSavePromptName("");
                  setSavePromptDescription("");
                  setSavePromptError(null);
                }}
                disabled={isSavingPrompt}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleSavePromptToLibrary}
                disabled={!savePromptName.trim() || isSavingPrompt}
                className="gap-1.5"
              >
                {isSavingPrompt ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Save Prompt
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Diff View - Collapsible */}
      {enhancedText && !isEnhancing && diffResult && (
        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
          <button
            type="button"
            onClick={() => setShowDiff(!showDiff)}
            className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {showDiff ? (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-500" />
            )}
            <GitCompare className="h-4 w-4 text-gray-500" />
            <span>View Changes</span>
            <span className="ml-auto text-xs text-gray-400">
              {diffResult.filter(p => p.added).length} additions, {diffResult.filter(p => p.removed).length} removals
            </span>
          </button>

          {showDiff && (
            <div className="border-t border-gray-200 p-4 max-h-[400px] overflow-y-auto">
              <div className="text-sm leading-relaxed whitespace-pre-wrap font-mono">
                {diffResult.map((part, index) => {
                  if (part.added) {
                    return (
                      <span
                        key={index}
                        className="bg-green-100 text-green-800 px-0.5 rounded"
                      >
                        {part.value}
                      </span>
                    );
                  }
                  if (part.removed) {
                    return (
                      <span
                        key={index}
                        className="bg-red-100 text-red-800 line-through px-0.5 rounded"
                      >
                        {part.value}
                      </span>
                    );
                  }
                  return <span key={index}>{part.value}</span>;
                })}
              </div>
              <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 bg-green-100 rounded"></span>
                  Added text
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 bg-red-100 rounded"></span>
                  Removed text
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Help Text */}
      {!enhancedText && !isEnhancing && (
        <p className="text-xs text-gray-500">
          Enter or paste your text in the left panel, then click "Enhance with AI"
          to generate an improved version.
        </p>
      )}
    </div>
  );
}

export default WritingAssistant;
